import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, Copy, LogIn, LogOut, Mail, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { API, errMsg } from "@/lib/api";

const AFFILIATE_TOKEN = "edm_affiliate_token";
const money = (cents = 0) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(cents / 100);
const date = (value) => value ? new Date(value).toLocaleDateString("es-ES") : "—";

function AffiliateHeader({ access = false }) {
  return (
    <header className="px-5 sm:px-8 py-5 border-b border-[#d9cbb8]">
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
        <Link to="/" aria-label="En Dos Mundos, inicio"><Logo /></Link>
        <Link to={access ? "/colabora" : "/colabora/acceso"} className="text-sm font-semibold underline underline-offset-4">
          {access ? "Información del programa" : "Área profesional"}
        </Link>
      </div>
    </header>
  );
}

const inputClass = "w-full rounded-lg border border-[#bcae9c] bg-white px-3 py-3 outline-none focus-visible:ring-2 focus-visible:ring-[var(--sage-deep)]";
const labelClass = "block text-sm font-semibold text-[var(--ink)]";

export function AffiliateJoin() {
  const [values, setValues] = useState({ name: "", email: "", profession: "", specialty: "", college_number: "", website: "", instagram: "", follower_count: "", presentation: "", terms_accepted: false, privacy_accepted: false });
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [confirmationEmailSent, setConfirmationEmailSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const update = (key) => (event) => setValues((current) => ({ ...current, [key]: event.target.type === "checkbox" ? event.target.checked : event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/affiliates/apply`, values);
      setConfirmationEmailSent(data.confirmation_email_sent === true);
      setSubmitted(true);
    } catch (requestError) {
      setError(errMsg(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      <AffiliateHeader />
      <section className="px-5 sm:px-8 py-14 sm:py-20 border-b border-[#d9cbb8]">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-end">
          <div>
            <p className="text-sm font-semibold uppercase text-[var(--sage-deep)]">Colaboración profesional</p>
            <h1 className="font-display text-4xl sm:text-6xl leading-tight mt-3 max-w-3xl">Tus familias necesitan recursos de calidad. Nosotros los creamos.</h1>
            <p className="font-display text-2xl sm:text-3xl text-[var(--terra)] mt-5">Tú puedes recomendarlos y obtener ingresos por cada venta.</p>
          </div>
          <div className="max-w-xl lg:justify-self-end">
            <p className="text-lg text-[#5a4f46]">En Dos Mundos desarrolla talleres digitales psicoeducativos para la vida familiar. Colaboramos con profesionales de la infancia y la familia mediante enlaces de recomendación medibles, sin coste inicial ni cuotas mensuales.</p>
            <a href="#solicitud" className="btn-accent mt-6">Solicitar colaboración <ArrowUpRight size={18} /></a>
          </div>
        </div>
      </section>

      <section id="soluciones" className="px-5 sm:px-8 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm font-semibold uppercase text-[var(--sage-deep)]">Recursos digitales</p>
          <h2 className="font-display text-3xl sm:text-4xl mt-2">Material práctico para situaciones cotidianas.</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-8">
            <article className="border-t-2 border-[var(--sage-deep)] pt-5">
              <h3 className="font-display text-2xl">Equipo en Calma</h3>
              <p className="mt-2 text-[#5a4f46]">Un recorrido de 21 días con ejercicios breves sobre comunicación, acuerdos y cooperación en la crianza. Incluye materiales imprimibles y acompañamiento educativo asíncrono.</p>
              <p className="mt-3 text-sm font-semibold">Para familias con hijos e hijas de 0 a 5 años.</p>
            </article>
            <article className="border-t-2 border-[var(--terra)] pt-5">
              <h3 className="font-display text-2xl">Rabietas: acompañar con límites</h3>
              <p className="mt-2 text-[#5a4f46]">Un taller autoguiado de siete días con ejemplos, casos interactivos y planificadores sobre frustración, seguridad, límites y transiciones.</p>
              <p className="mt-3 text-sm font-semibold">Para familias con niños y niñas de 2 a 3 años.</p>
            </article>
          </div>
          <p className="mt-8 max-w-3xl text-sm text-[#5a4f46]">Son recursos psicoeducativos, no incluyen diagnóstico ni atención psicológica individual y no sustituyen el criterio profesional ni una valoración clínica.</p>
        </div>
      </section>

      <section className="bg-[var(--sage-deep)] text-white px-5 sm:px-8 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto grid md:grid-cols-[0.8fr_1.2fr] gap-10">
          <div>
            <p className="text-sm font-semibold uppercase text-white/75">Cómo funciona</p>
            <h2 className="font-display text-3xl sm:text-4xl mt-2">Recomienda con claridad. Consulta cada resultado.</h2>
          </div>
          <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
            {[
              ["01", "Envía tu solicitud", "Revisamos cada perfil manualmente y confirmamos por correo la decisión."],
              ["02", "Recibe tu enlace", "Si se aprueba, activas tu acceso y encuentras enlaces, materiales y condiciones."],
              ["03", "Comparte a tu manera", "Las compras se atribuyen durante 30 días desde el enlace, cuando la persona acepta la medición de afiliación."],
              ["04", "Revisa y liquida", "Las ventas y comisiones aparecen en el panel. Las comisiones se validan tras el periodo de devolución y se registran los pagos."],
            ].map(([number, title, copy]) => (
              <li key={number} className="border-t border-white/35 pt-4">
                <span className="text-sm text-white/70">{number}</span>
                <h3 className="font-display text-xl mt-1">{title}</h3>
                <p className="text-sm text-white/80 mt-1">{copy}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[0.8fr_1.2fr] gap-10">
          <div>
            <p className="text-sm font-semibold uppercase text-[var(--sage-deep)]">Condiciones económicas</p>
            <h2 className="font-display text-3xl sm:text-4xl mt-2">25 % de comisión propuesta.</h2>
            <p className="mt-4 text-[#5a4f46]">Sin inversión inicial, cuotas ni obligación de compra. La comisión se calcula sobre el importe efectivamente cobrado, incluidos los impuestos aplicables, después de descuentos; las cancelaciones y devoluciones reducen o anulan la comisión correspondiente.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="p-5 bg-white border border-[#d9cbb8] rounded-lg">
              <p className="text-sm text-[#5a4f46]">Ejemplo ilustrativo · taller de 39 €</p>
              <p className="font-display text-3xl mt-2">9,75 €</p>
              <p className="text-xs text-[#7a6c5e] mt-2">Si 39 € es la base de cálculo y no hay descuento ni devolución.</p>
            </div>
            <div className="p-5 bg-white border border-[#d9cbb8] rounded-lg">
              <p className="text-sm text-[#5a4f46]">Ejemplo ilustrativo · taller de 79 €</p>
              <p className="font-display text-3xl mt-2">19,75 €</p>
              <p className="text-xs text-[#7a6c5e] mt-2">Si 79 € es la base de cálculo y no hay descuento ni devolución.</p>
            </div>
          </div>
          <p className="lg:col-start-2 text-xs text-[#7a6c5e]">La base, el periodo de validación, el método y el calendario de pago se detallan en las condiciones del programa antes de aceptar la colaboración. Ejemplos no vinculantes.</p>
        </div>
      </section>

      <section id="solicitud" className="px-5 sm:px-8 py-12 sm:py-16 bg-[var(--cream-2)] border-y border-[#d9cbb8]">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[0.8fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-8">
            <p className="text-sm font-semibold uppercase text-[var(--sage-deep)]">Solicitud profesional</p>
            <h2 className="font-display text-3xl sm:text-4xl mt-2">Cuéntanos sobre tu trabajo.</h2>
            <p className="mt-4 text-[#5a4f46]">Revisamos las solicitudes manualmente. Enviar el formulario no activa una cuenta ni implica una aprobación.</p>
            <p className="mt-5 text-sm text-[#5a4f46] inline-flex gap-2"><ShieldCheck size={18} className="shrink-0" /> Tus datos se utilizarán para evaluar y gestionar la solicitud de colaboración.</p>
          </div>
          {submitted ? (
            <div role="status" className="bg-white border border-[#d9cbb8] p-6 sm:p-8 rounded-lg">
              <Mail className="text-[var(--sage-deep)]" size={24} />
              <h3 className="font-display text-2xl mt-4">Solicitud recibida</h3>
              <p className="mt-2 text-[#5a4f46]">{confirmationEmailSent ? "Te hemos enviado una confirmación al correo indicado." : "La solicitud quedó registrada, pero el correo automático no está disponible ahora. Contacta con endosmundos.digital@gmail.com para confirmar su recepción."} La revisión es manual; recibirás una respuesta cuando se tome una decisión.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white p-5 sm:p-8 rounded-lg border border-[#d9cbb8] grid sm:grid-cols-2 gap-5">
              <label className={labelClass}>Nombre y apellidos<input className={`${inputClass} mt-2`} autoComplete="name" value={values.name} onChange={update("name")} required maxLength={160} /></label>
              <label className={labelClass}>Correo electrónico<input className={`${inputClass} mt-2`} type="email" autoComplete="email" value={values.email} onChange={update("email")} required /></label>
              <label className={labelClass}>Profesión<input className={`${inputClass} mt-2`} value={values.profession} onChange={update("profession")} placeholder="Psicóloga, educadora…" required /></label>
              <label className={labelClass}>Especialidad<input className={`${inputClass} mt-2`} value={values.specialty} onChange={update("specialty")} placeholder="Infancia, perinatalidad, familia…" required /></label>
              <label className={labelClass}>N.º de colegiación, cuando corresponda<input className={`${inputClass} mt-2`} value={values.college_number} onChange={update("college_number")} /></label>
              <label className={labelClass}>Seguidores aproximados<input className={`${inputClass} mt-2`} value={values.follower_count} onChange={update("follower_count")} placeholder="Opcional" /></label>
              <label className={labelClass}>Página web<input className={`${inputClass} mt-2`} type="url" value={values.website} onChange={update("website")} placeholder="https://" /></label>
              <label className={labelClass}>Perfil de Instagram<input className={`${inputClass} mt-2`} value={values.instagram} onChange={update("instagram")} placeholder="@perfil" /></label>
              <label className={`${labelClass} sm:col-span-2`}>Presentación profesional<textarea className={`${inputClass} mt-2 min-h-32 resize-y`} value={values.presentation} onChange={update("presentation")} required minLength={20} maxLength={2000} /></label>
              <label className="sm:col-span-2 flex items-start gap-3 text-sm text-[#5a4f46]"><input className="mt-1 accent-[var(--sage-deep)]" type="checkbox" checked={values.terms_accepted} onChange={update("terms_accepted")} required /><span>He leído y acepto las <Link to="/colabora/condiciones" target="_blank" className="font-semibold underline">condiciones del programa</Link>.</span></label>
              <label className="sm:col-span-2 flex items-start gap-3 text-sm text-[#5a4f46]"><input className="mt-1 accent-[var(--sage-deep)]" type="checkbox" checked={values.privacy_accepted} onChange={update("privacy_accepted")} required /><span>He leído la <Link to="/politica-de-privacidad" target="_blank" className="font-semibold underline">política de privacidad</Link> y autorizo el tratamiento de los datos para gestionar mi solicitud.</span></label>
              {error && <p role="alert" className="sm:col-span-2 text-sm text-red-700">{error}</p>}
              <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4">
                <button className="btn-accent" type="submit" disabled={busy}>{busy ? "Enviando…" : "Enviar solicitud"}</button>
                <p className="text-xs text-[#7a6c5e]">No hay coste de inscripción ni cuota mensual.</p>
              </div>
            </form>
          )}
        </div>
      </section>
      <footer className="px-5 sm:px-8 py-8 max-w-6xl mx-auto flex flex-wrap gap-5 text-sm">
        <Link to="/" className="inline-flex items-center gap-2 underline"><ArrowLeft size={16} /> En Dos Mundos</Link>
        <Link to="/colabora/condiciones" className="underline">Condiciones del programa</Link>
        <Link to="/politica-de-privacidad" className="underline">Privacidad</Link>
      </footer>
    </main>
  );
}

export function AffiliateTerms() {
  return (
    <main className="min-h-screen bg-[var(--cream)] px-5 sm:px-8 py-8 text-[var(--ink)]">
      <div className="max-w-3xl mx-auto">
        <Link to="/colabora" className="inline-flex items-center gap-2 text-sm underline"><ArrowLeft size={16} /> Volver al programa</Link>
        <h1 className="font-display text-4xl sm:text-5xl mt-8">Condiciones del programa piloto</h1>
        <p className="text-sm text-[#7a6c5e] mt-3">Programa de colaboración profesional de En Dos Mundos.</p>
        <div className="mt-8 space-y-6 text-[#5a4f46]">
          <section><h2 className="font-display text-2xl text-[var(--ink)]">Participación</h2><p className="mt-2">No hay coste de alta ni cuotas mensuales. Cada solicitud se revisa manualmente. La profesional puede dejar de participar cuando quiera; En Dos Mundos puede suspender la colaboración por fraude o comunicación engañosa.</p></section>
          <section><h2 className="font-display text-2xl text-[var(--ink)]">Recomendaciones y atribución</h2><p className="mt-2">Cada profesional recibe un enlace y un código personal. Para las ventas que se cierran por WhatsApp, la persona compradora debe conservar el código en el mensaje y el equipo lo confirmará al registrar el pago. Los enlaces pueden atribuir una visita durante 30 días cuando la persona acepta la medición de afiliación. Si no acepta, la atribución se realiza con el código que facilite al comprar.</p></section>
          <section><h2 className="font-display text-2xl text-[var(--ink)]">Comisión y liquidación</h2><p className="mt-2">La comisión es del 25 % del importe efectivamente cobrado, incluidos los impuestos aplicados y después de descuentos. No genera comisión una consulta, un pago fallido o una venta cancelada. Si hay devolución parcial, la comisión se ajusta; si es total, se anula.</p><p className="mt-2">La venta y su comisión quedan pendientes durante 30 días desde el pago para contemplar cancelaciones y devoluciones. Después se revisan y, si son válidas, se incluyen en la liquidación mensual por transferencia bancaria. No hay importe mínimo. La profesional facilitará los datos y justificantes fiscales que correspondan.</p></section>
          <section><h2 className="font-display text-2xl text-[var(--ink)]">Comunicación responsable</h2><p className="mt-2">Las profesionales deben presentar la colaboración de forma transparente cuando la normativa lo exija. No podrán asegurar resultados, atribuir efectos terapéuticos a los talleres ni presentarlos como diagnóstico, tratamiento o sustituto de atención sanitaria.</p></section>
          <section><h2 className="font-display text-2xl text-[var(--ink)]">Datos y acceso</h2><p className="mt-2">Cada profesional puede consultar solo sus propios datos. No se debe compartir información clínica ni datos que identifiquen a menores en el panel o los mensajes de recomendación. La política de privacidad explica el tratamiento de los datos de solicitud y de actividad.</p><p className="mt-2">La profesional es responsable de sus obligaciones fiscales y de informar de la relación comercial cuando corresponda.</p></section>
        </div>
      </div>
    </main>
  );
}

export function AffiliateActivate() {
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const token = params.get("token") || "";

  const submit = async (event) => {
    event.preventDefault();
    if (password !== confirm) return setError("Las contraseñas no coinciden.");
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/affiliates/activate`, { token, password });
      setDone(true);
    } catch (requestError) { setError(errMsg(requestError)); }
    finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      <AffiliateHeader access />
      <div className="px-5 py-14 max-w-md mx-auto">
        <h1 className="font-display text-3xl">Activa tu acceso profesional</h1>
        {done ? <div className="mt-6"><p>Contraseña creada. Ya puedes entrar al panel.</p><Link className="btn-accent mt-5" to="/colabora/acceso">Ir al acceso</Link></div> : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <label className={labelClass}>Nueva contraseña<input className={`${inputClass} mt-2`} type="password" minLength={12} maxLength={128} value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" /></label>
            <label className={labelClass}>Repite la contraseña<input className={`${inputClass} mt-2`} type="password" minLength={12} maxLength={128} value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" /></label>
            {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
            <button type="submit" disabled={busy || !token} className="btn-accent w-full">{busy ? "Activando…" : "Crear contraseña"}</button>
            {!token && <p className="text-sm text-red-700">Falta el enlace de activación. Solicita uno nuevo a administración.</p>}
          </form>
        )}
      </div>
    </main>
  );
}

export function AffiliateLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/affiliates/login`, { email, password });
      localStorage.setItem(AFFILIATE_TOKEN, data.token);
      navigate("/colabora/panel", { replace: true });
    } catch (requestError) { setError(errMsg(requestError)); }
    finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      <AffiliateHeader />
      <div className="px-5 py-14 max-w-md mx-auto">
        <p className="text-sm font-semibold uppercase text-[var(--sage-deep)]">Área profesional</p>
        <h1 className="font-display text-3xl mt-2">Accede a tu colaboración</h1>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <label className={labelClass}>Correo electrónico<input className={`${inputClass} mt-2`} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
          <label className={labelClass}>Contraseña<input className={`${inputClass} mt-2`} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <button type="submit" disabled={busy} className="btn-accent w-full"><LogIn size={18} /> {busy ? "Entrando…" : "Entrar"}</button>
        </form>
        <Link to="/colabora" className="inline-block mt-6 text-sm underline">Aún no colaboro: conocer el programa</Link>
      </div>
    </main>
  );
}

export function AffiliateDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [materials, setMaterials] = useState(null);
  const [error, setError] = useState("");
  const token = localStorage.getItem(AFFILIATE_TOKEN);

  useEffect(() => {
    if (!token) {
      navigate("/colabora/acceso", { replace: true });
      return;
    }
    const requestConfig = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${API}/affiliates/dashboard`, requestConfig),
      axios.get(`${API}/affiliates/materials`, requestConfig),
      axios.get(`${API}/affiliates/me`, requestConfig),
    ]).then(([dashboard, materialList, profile]) => {
      setData({ ...dashboard.data, affiliate: profile.data });
      setMaterials(materialList.data);
    })
      .catch((requestError) => {
        setError(errMsg(requestError));
        if ([401, 403].includes(requestError?.response?.status)) {
          localStorage.removeItem(AFFILIATE_TOKEN);
          navigate("/colabora/acceso", { replace: true });
        }
      });
  }, [navigate, token]);

  const logout = () => {
    localStorage.removeItem(AFFILIATE_TOKEN);
    navigate("/colabora/acceso", { replace: true });
  };

  if (!data) return <main className="min-h-screen px-5 py-12 text-center">{error || "Cargando tu panel…"}</main>;
  const totals = data.commission_totals_cents || {};
  const copy = async (text) => {
    await navigator.clipboard?.writeText(text);
  };

  return (
    <main className="min-h-screen bg-[var(--cream)] text-[var(--ink)] px-5 sm:px-8 py-6">
      <div className="max-w-6xl mx-auto">
        <header className="flex items-center justify-between gap-4 border-b border-[#d9cbb8] pb-5">
          <div><Link to="/" aria-label="Inicio"><Logo /></Link><p className="text-sm text-[#5a4f46] mt-2">Panel profesional · {data.affiliate.name}</p></div>
          <button onClick={logout} className="text-sm underline inline-flex items-center gap-2"><LogOut size={16} /> Salir</button>
        </header>
        {error && <p role="alert" className="mt-4 text-sm text-red-700">{error}</p>}
        <section className="mt-8">
          <h1 className="font-display text-3xl">Tu actividad</h1>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[["Clics", data.clicks], ["Pendientes", money(totals.pending)], ["Confirmadas", money(totals.confirmed)], ["Abonadas", money(totals.paid)]].map(([label, value]) => (
              <div key={label} className="bg-white border border-[#d9cbb8] rounded-lg p-4"><p className="text-sm text-[#5a4f46]">{label}</p><p className="font-display text-2xl mt-1">{value}</p></div>
            ))}
          </div>
        </section>
        <section className="mt-8 border-t border-[#d9cbb8] pt-6">
          <h2 className="font-display text-2xl">Enlaces personales</h2>
          <p className="mt-1 text-sm text-[#5a4f46]">Tu código: <strong>{data.affiliate.referral_code}</strong> · Comisión: {data.affiliate.commission_rate}%{data.affiliate.coupon_code ? ` · Código promocional: ${data.affiliate.coupon_code}` : ""}</p>
          <div className="mt-4 space-y-2">
            {(materials?.links || []).map((item) => <div key={item.name} className="flex flex-col sm:flex-row sm:items-center gap-2 bg-white p-3 border border-[#d9cbb8] rounded-lg"><span className="font-semibold sm:w-56">{item.name}</span><a className="text-sm underline break-all flex-1" href={item.url}>{item.url}</a><button type="button" title="Copiar enlace" onClick={() => copy(item.url)} className="p-2 self-start"><Copy size={17} /></button></div>)}
          </div>
        </section>
        <section className="mt-8 border-t border-[#d9cbb8] pt-6">
          <h2 className="font-display text-2xl">Materiales para compartir</h2>
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            {(materials?.products || []).map((product) => <article key={product.name} className="bg-white border border-[#d9cbb8] rounded-lg overflow-hidden">
              <img src={product.image} alt={`Imagen promocional de ${product.name}`} loading="lazy" className="w-full aspect-[16/8] object-cover" />
              <div className="p-4"><h3 className="font-display text-xl">{product.name}</h3><p className="mt-2 text-sm text-[#5a4f46]">{product.description}</p><a className="mt-3 inline-block text-sm font-semibold underline" href={product.image} download>Descargar imagen</a></div>
            </article>)}
          </div>
          <div className="mt-4 grid md:grid-cols-2 gap-5">
            <div><h3 className="font-semibold">Textos</h3>{(materials?.instagram_copy || []).map((text) => <article key={text} className="mt-2 bg-white p-4 border border-[#d9cbb8] rounded-lg"><p className="text-sm">{text}</p><button className="mt-3 text-sm underline inline-flex gap-2 items-center" onClick={() => copy(text)}><Copy size={15} /> Copiar texto</button></article>)}</div>
            <div><h3 className="font-semibold">Historias</h3>{(materials?.stories || []).map((text) => <article key={text} className="mt-2 bg-white p-4 border border-[#d9cbb8] rounded-lg"><p className="text-sm">{text}</p><button className="mt-3 text-sm underline inline-flex gap-2 items-center" onClick={() => copy(text)}><Copy size={15} /> Copiar historia</button></article>)}<a className="mt-4 inline-flex text-sm underline items-center gap-2" href={materials?.terms_url}><ArrowUpRight size={15} /> Condiciones de colaboración</a></div>
          </div>
          <div className="mt-6 border-t border-[#d9cbb8] pt-5 max-w-3xl">
            <h3 className="font-semibold">Preguntas frecuentes</h3>
            {(materials?.faq || []).map((item) => <details key={item.question} className="border-b border-[#d9cbb8] py-3"><summary className="cursor-pointer text-sm font-semibold">{item.question}</summary><p className="mt-2 text-sm text-[#5a4f46]">{item.answer}</p></details>)}
          </div>
        </section>
        <section className="mt-8 border-t border-[#d9cbb8] pt-6">
          <h2 className="font-display text-2xl">Ventas y comisiones</h2>
          <div className="mt-4 overflow-x-auto bg-white border border-[#d9cbb8] rounded-lg">
            <table className="w-full min-w-[620px] text-sm text-left"><thead><tr className="border-b border-[#d9cbb8] text-[#5a4f46]"><th className="p-3">Fecha</th><th className="p-3">Producto</th><th className="p-3">Venta</th><th className="p-3">Estado</th></tr></thead><tbody>{data.orders.map((order) => <tr key={order.id} className="border-b border-[#eee4d8]"><td className="p-3">{date(order.created_at)}</td><td className="p-3">{order.product_name}</td><td className="p-3">{money(order.amount_cents)}</td><td className="p-3">{order.status}</td></tr>)}</tbody></table>
            {!data.orders.length && <p className="p-4 text-sm text-[#7a6c5e]">Todavía no hay ventas atribuidas.</p>}
          </div>
          <div className="mt-4 overflow-x-auto bg-white border border-[#d9cbb8] rounded-lg">
            <table className="w-full min-w-[700px] text-sm text-left"><thead><tr className="border-b border-[#d9cbb8] text-[#5a4f46]"><th className="p-3">Fecha</th><th className="p-3">Producto</th><th className="p-3">Base</th><th className="p-3">Comisión</th><th className="p-3">Estado</th></tr></thead><tbody>{data.commissions.map((commission) => <tr key={commission.id} className="border-b border-[#eee4d8]"><td className="p-3">{date(commission.created_at)}</td><td className="p-3">{commission.product_name}</td><td className="p-3">{money(commission.base_cents)}</td><td className="p-3">{money(commission.amount_cents)}</td><td className="p-3">{commission.status}</td></tr>)}</tbody></table>
            {!data.commissions.length && <p className="p-4 text-sm text-[#7a6c5e]">Las comisiones aparecerán cuando haya compras válidas.</p>}
          </div>
        </section>
        <section className="mt-8 border-t border-[#d9cbb8] pt-6 pb-10">
          <h2 className="font-display text-2xl">Liquidaciones</h2>
          <div className="mt-4 overflow-x-auto bg-white border border-[#d9cbb8] rounded-lg">
            <table className="w-full min-w-[500px] text-sm text-left"><thead><tr className="border-b border-[#d9cbb8] text-[#5a4f46]"><th className="p-3">Fecha</th><th className="p-3">Importe</th><th className="p-3">Referencia</th></tr></thead><tbody>{data.payouts.map((payout) => <tr key={payout.id} className="border-b border-[#eee4d8]"><td className="p-3">{date(payout.paid_at)}</td><td className="p-3">{money(payout.amount_cents)}</td><td className="p-3">{payout.payment_reference}</td></tr>)}</tbody></table>
            {!data.payouts.length && <p className="p-4 text-sm text-[#7a6c5e]">Todavía no hay liquidaciones registradas.</p>}
          </div>
        </section>
      </div>
    </main>
  );
}
